import FormLoader from '../FormLoader/FormLoader';
import Home from '../../Pages/Home';
import {
  getFormDepartment,
  requiresManagerSignature,
} from '../../config/formRegistry';

/**
 * Render form component by name using lazy loading
 * All form components are code-split for better performance
 *
 * @param {string} formName - Name of the form to render
 * @param {object} payload - Current form data
 * @param {function} setPayLoad - State setter for form data
 * @param {boolean} submitSuccess - Submit success status
 * @param {boolean} submitFailed - Submit failed status
 * @param {boolean} submitStatus - Submit status
 * @param {function} setIdBadgeFile - File setter for NewHire
 * @param {function} setGovIdFront - File setter for NewHire
 * @param {function} setGovIdRear - File setter for NewHire
 * @param {function} setSsnCard - File setter for NewHire
 * @param {boolean} formError - Form validation error state
 * @param {function} setFormError - Error state setter
 * @param {string} errMsg - Error message
 * @returns {JSX.Element} Form component or Home
 */
export const renderComponentByFormName = (
  formName,
  payload,
  setPayLoad,
  submitSuccess,
  submitFailed,
  submitStatus,
  setIdBadgeFile,
  setGovIdFront,
  setGovIdRear,
  setSsnCard,
  formError,
  setFormError,
  errMsg
) => {
  // Build common props object
  const commonProps = {
    setPayLoad,
    payload,
    submitSuccess,
    submitFailed,
    submitStatus,
    formError,
    setFormError,
    errMsg,
  };

  // Add file props for NewHire form
  if (formName === 'newHire') {
    commonProps.setIdBadgeFile = setIdBadgeFile;
    commonProps.setGovIdFrontFile = setGovIdFront;
    commonProps.setGovIdRearFile = setGovIdRear;
    commonProps.setSsnCardFile = setSsnCard;
  }

  // Return FormLoader with lazy-loaded component or Home as default
  return formName ? (
    <FormLoader formName={formName} {...commonProps} />
  ) : (
    <Home />
  );
};

/**
 * Get department for a form using the centralized registry
 * @param {string} formName - The name of the form
 * @returns {string|null} Department name
 * @deprecated Use getFormDepartment from formRegistry directly
 */
export const formDepartment = (formName) => {
  return getFormDepartment(formName);
};

/**
 * Check if form requires manager signature pad using the centralized registry
 * @param {string} formName - The name of the form
 * @returns {boolean}
 * @deprecated Use requiresManagerSignature from formRegistry directly
 */
export const showManagerSigPad = (formName) => {
  return requiresManagerSignature(formName);
};

/**
 * Find a store by ID from an array of stores
 * Centralizes the duplicated filterStore pattern from form components
 *
 * @param {Array} stores - Array of store objects
 * @param {string|number} id - Store ID to find
 * @returns {object|undefined} Found store or undefined
 */
export const findStoreById = (stores, id) => {
  if (!stores || !id) return undefined;
  return stores.find((store) => store.id === parseInt(id, 10));
};

/**
 * Check if a field has validation errors
 * Centralizes the duplicated checkInputValid pattern from form components
 *
 * @param {string} fieldName - Name of the field to check
 * @param {Array} fieldErrors - Array of error objects with 'input' property
 * @returns {boolean} True if field is valid (no errors)
 * @deprecated Use hasFieldError from useFormValidation hook instead
 */
export const isFieldValid = (fieldName, fieldErrors) => {
  if (!fieldErrors || fieldErrors.length === 0) return true;
  return fieldErrors.findIndex((x) => x.input === fieldName) === -1;
};
